import { useState, useMemo, useEffect } from "react";
import { ArrowLeft, Building2 } from "lucide-react";
import { useAuth } from "../../hooks/useAuth";

const ENV_SSO_DOMAINS = new Set<string>(
  (import.meta.env.VITE_SSO_DOMAINS || "")
    .split(",")
    .map((domain: string) => domain.trim().toLowerCase())
    .filter(Boolean)
);

const SSO_DOMAINS_CACHE_KEY = "sso_domains";
const SSO_DOMAINS_CACHE_TTL = 5 * 60 * 1000;

function loadCachedDomains(): string[] {
  try {
    const cached = localStorage.getItem(SSO_DOMAINS_CACHE_KEY);
    if (cached) {
      const { domains, expiresAt } = JSON.parse(cached);
      if (Date.now() < expiresAt && Array.isArray(domains)) {
        return domains;
      }
    }
  } catch {
    // Corrupt cache - ignore
  }
  return [];
}

interface SSOLoginDialogProps {
  onBack: () => void;
  isDark?: boolean;
}

export const SSOLoginDialog = ({ onBack }: SSOLoginDialogProps) => {
  const auth = useAuth();
  const [email, setEmail] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [apiDomains, setApiDomains] = useState<string[]>(loadCachedDomains);

  // Fetch SSO domains from backend API on mount
  useEffect(() => {
    const apiUrl = import.meta.env.VITE_API_INTERNAL_URL;
    if (!apiUrl) return;

    fetch(`${apiUrl}/public/sso/domains`)
      .then((res) => {
        if (!res.ok) throw new Error("Failed to fetch SSO domains");
        return res.json();
      })
      .then((domains: string[]) => {
        setApiDomains(domains);
        localStorage.setItem(
          SSO_DOMAINS_CACHE_KEY,
          JSON.stringify({
            domains,
            expiresAt: Date.now() + SSO_DOMAINS_CACHE_TTL,
          })
        );
      })
      .catch(() => {
        // Fail silently
      });
  }, []);

  const ssoDomains = useMemo(
    () => new Set([...ENV_SSO_DOMAINS, ...apiDomains]),
    [apiDomains]
  );

  const handleSSOSubmit = () => {
    const trimmedEmail = email.trim().toLowerCase();

    if (!trimmedEmail || !trimmedEmail.includes("@")) {
      setError("Please enter a valid email address");
      return;
    }

    const domain = trimmedEmail.split("@")[1];
    if (!ssoDomains.has(domain)) {
      setError(
        `SSO is not configured for ${domain}. Please contact your administrator.`
      );
      return;
    }

    setIsLoading(true);
    setError("");

    auth.signinRedirect({
      extraQueryParams: {
        identity_provider: "Clerk",
        login_hint: trimmedEmail,
      },
    });
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") handleSSOSubmit();
  };

  return (
    <div className="flex flex-col gap-3">
      <button
        onClick={onBack}
        className="flex items-center gap-1 font-normal transition-colors self-start hover:opacity-80 font-body-primary text-login-subtext"
      >
        <ArrowLeft className="h-4 w-4" />
        Back to sign in
      </button>

      <div className="flex items-center gap-2 mb-1">
        <Building2 className="login-icon text-login-accent" />
        <h2 className="font-semibold font-heading-primary text-login-heading">
          Sign in with SSO
        </h2>
      </div>

      <p className="font-normal font-body-primary text-login-subtext">
        Enter your work email address to sign in with your company's identity
        provider.
      </p>

      {error && (
        <div className="p-3 border rounded-lg font-body-primary bg-login-error-bg border-login-error-border text-login-error-text">
          {error}
        </div>
      )}

      <div>
        <label className="block font-medium mb-1 font-body-primary text-login-label">
          Work email <span className="text-red-500">*</span>
        </label>
        <input
          type="email"
          value={email}
          onChange={(e) => {
            setEmail(e.target.value);
            setError("");
          }}
          placeholder="you@company.com"
          autoFocus
          className="w-full border rounded-lg outline-none transition-shadow focus:ring-2 focus:ring-login-accent focus:border-login-accent login-input bg-login-input-bg border-login-input-border text-login-input-text login-input-sm-padding"
          onKeyDown={handleKeyDown}
        />
      </div>

      <button
        onClick={handleSSOSubmit}
        disabled={isLoading || !email.trim()}
        className="w-full rounded-md font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed hover:opacity-90 flex items-center justify-center login-btn bg-login-btn-bg text-login-btn-text"
      >
        {isLoading ? (
          <span className="flex items-center justify-center gap-2">
            <svg
              className="animate-spin h-5 w-5"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              />
            </svg>
            Redirecting...
          </span>
        ) : (
          "Continue with SSO"
        )}
      </button>
    </div>
  );
};
